// ═══════════════════════════════════════════════════════════════════════════
// SINGLE SOURCE OF TRUTH — Decision State Classifier
// ───────────────────────────────────────────────────────────────────────────
// This is the ONE function that every module (Snapshot, Growth vs Model,
// Spreadsheet) and every downstream consumer (Wingman, reports, summaries,
// PDFs) must use to determine the final decision state.
//
// No component may derive its own decision class. All secondary logic — the
// old inline `actionabilityLevel` ternary in generateIntelligence.js and the
// ad-hoc Snapshot panel rules — routes through this function.
//
// OUTPUT STATES (exactly three — no others permitted):
//   ACTIONABLE          — Strong confirmed signal. Operational review supported.
//   VALIDATION_REQUIRED — Signal present but with caveats. Confirm before acting.
//   MONITOR_ONLY        — Insufficient confidence. No operational action.
//
// CONSTRAINTS:
//   • Single snapshots can NEVER be ACTIONABLE.
//   • Any conflict or anomaly forces at least VALIDATION_REQUIRED.
//   • Severe data issues (reliability < 50 or suspected error) force MONITOR_ONLY.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * @typedef {Object} DecisionStateInputs
 * @property {number}  reliabilityScore     0–100 (dataQuality.score)
 * @property {string}  detectionStrength    'High' | 'Moderate' | 'Low'
 * @property {boolean} hasConflict          Any FCR/Jump/Growth conflict
 * @property {boolean} hasSuspectedError    Sample error flag
 * @property {boolean} highVolatility       FCR volatility flag
 * @property {boolean} hasFeedData          Feed data available for financial layer
 * @property {number}  sampleCount          Number of weight samples
 * @property {boolean} isSnapshot           True for single-snapshot analyses
 * @property {boolean} hasMissingFish       Detection indicates missing fish
 */

/**
 * @typedef {Object} DecisionState
 * @property {'ACTIONABLE'|'VALIDATION_REQUIRED'|'MONITOR_ONLY'} decisionClass
 * @property {string}   reason
 * @property {string[]} downgradeReasons
 * @property {boolean}  capApplied  True when a hard cap (snapshot / severe / conflict) changed the result
 */

const CLASS = Object.freeze({
  ACTIONABLE: 'ACTIONABLE',
  VALIDATION_REQUIRED: 'VALIDATION_REQUIRED',
  MONITOR_ONLY: 'MONITOR_ONLY',
});

/**
 * Resolve the single canonical decision state.
 * @param {DecisionStateInputs} inputs
 * @returns {DecisionState}
 */
export function resolveDecisionState(inputs = {}) {
  const {
    reliabilityScore = 50,
    detectionStrength = 'Low',
    hasConflict = false,
    hasSuspectedError = false,
    highVolatility = false,
    hasFeedData = true,
    sampleCount = 0,
    isSnapshot = false,
  } = inputs;

  const downgradeReasons = [];
  // Snapshots inherently have low reliability (single sample). Don't double-penalise
  // them via the generic reliability floor — their constraint is handled by the
  // dedicated "isSnapshot never ACTIONABLE" cap below.
  if (!isSnapshot && reliabilityScore < 50) downgradeReasons.push('Data reliability below threshold (< 50)');
  if (hasSuspectedError) downgradeReasons.push('Suspected sample error detected');
  if (highVolatility) downgradeReasons.push('High sample volatility');
  if (!hasFeedData) downgradeReasons.push('No feed data — FCR conclusions limited');
  if (!isSnapshot && sampleCount > 0 && sampleCount < 5) downgradeReasons.push('Low evidence depth (< 5 samples)');
  if (hasConflict) downgradeReasons.push('Conflicting signals — FCR vs Growth/Jump');
  if (isSnapshot) downgradeReasons.push('Single snapshot — no trend confirmation');

  const severeDowngrade = (!isSnapshot && reliabilityScore < 50) || hasSuspectedError;

  // ── Severe data issues → MONITOR_ONLY regardless of signal strength ──
  if (severeDowngrade) {
    return {
      decisionClass: CLASS.MONITOR_ONLY,
      reason: `${downgradeReasons[0]}. Continue routine monitoring — no operational action recommended.`,
      downgradeReasons,
      capApplied: true,
    };
  }

  // ── Conflict present → cap at VALIDATION_REQUIRED ──
  if (hasConflict) {
    return {
      decisionClass: CLASS.VALIDATION_REQUIRED,
      reason: 'Conflicting signals between detection methods. Confirm before any operational change.',
      downgradeReasons,
      capApplied: true,
    };
  }

  // ── Snapshot mode → never ACTIONABLE ──
  if (isSnapshot) {
    const snapClass = detectionStrength !== 'Low'
      ? CLASS.VALIDATION_REQUIRED
      : CLASS.MONITOR_ONLY;
    return {
      decisionClass: snapClass,
      reason: snapClass === CLASS.VALIDATION_REQUIRED
        ? `${detectionStrength} signal from single snapshot. Field validation required — operational changes are not recommended at this confidence level.`
        : 'Single snapshot — monitoring only. Operational changes are not recommended at this confidence level.',
      downgradeReasons,
      capApplied: true,
    };
  }

  // ── Multi-sample classification ──
  const hasAnyDowngrade = downgradeReasons.length > 0;

  if (detectionStrength === 'High' && reliabilityScore >= 70 && !hasAnyDowngrade) {
    return {
      decisionClass: CLASS.ACTIONABLE,
      reason: 'Strong detection signal with good data quality. Operational review supported.',
      downgradeReasons,
      capApplied: false,
    };
  }

  if (detectionStrength === 'High' || detectionStrength === 'Moderate') {
    return {
      decisionClass: CLASS.VALIDATION_REQUIRED,
      reason: detectionStrength === 'High'
        ? 'Strong detection signal but reliability concerns present. Field validation recommended.'
        : 'Moderate detection signal. Further confirmation needed before operational changes.',
      downgradeReasons,
      capApplied: false,
    };
  }

  return {
    decisionClass: CLASS.MONITOR_ONLY,
    reason: 'Low detection strength. Continue routine monitoring.',
    downgradeReasons,
    capApplied: false,
  };
}

export const DECISION_CLASS = CLASS;
